import { request } from './client'
import type { ConsentStatus, ParentAuth } from './parent'

/**
 * P1, P2, P3 and S3 — the consent record and the two subject rights.
 *
 * Kept apart from `parent.ts` because these are the routes an auditor asks to
 * see, and because S3 is a *school* screen reading the same records: one module
 * for "consent and rights", whoever is looking at them.
 */

export interface ConsentView {
  playerId: string
  childName: string
  status: ConsentStatus
  grantedAt: string | null
  revokedAt: string | null
  retainUntil: string | null
  verificationMethod: string | null
  /** P3-31 — the notice the parent agreed to (null: before notices were versioned). */
  noticeVersion?: string | null
  purposes?: string[]
  /**
   * 🔴 THE LIMITATION, CARRIED IN THE PAYLOAD. Revoking stops recording from
   * the NEXT session and deletes nothing already written — §9.3's retention job
   * does that. The screen must say so; a revoke button that silently leaves
   * last term's data in place is a right that exists on paper.
   */
  historyRemainsUntilRetentionRuns: boolean
}

/** P3-31 / D2 — the notice shown before "Keep progress", exactly as the server sends it. */
export interface ConsentNotice {
  version: string
  purposes: string[]
  title: string
  sections: { heading: string; body: string }[]
  retentionDays: number
  /** Who to write to with a question or complaint; null until Kalqy sets one. */
  grievanceContact: string | null
}

export interface ConsentClassRow {
  playerId: string
  name: string
  rollNumber: string | null
  status: ConsentStatus
  /** `phone-otp` (the parent, in the app) or `school-attested` (recorded from the school’s signed form). */
  verificationMethod?: string | null
  grantedAt: string | null
  revokedAt: string | null
  retainUntil: string | null
}

export interface ErasureResult {
  playerId: string
  deleted: {
    events: number
    sessions: number
    insights: number
    progress: number
    /** The teacher↔parent conversation goes too. */
    messages?: number
    questionAttempts?: number
    derived?: { stickerAwards: number; dayStats: number; gameDayStats?: number; assignmentTargets: number; progressCardSignoffs: number }
  }
  /** 🔴 The enrolment record stays — see the backend's `RightsService.erase`. */
  playerRecordRetained: true
  consentRevoked: boolean
}

/** The portability document. Deliberately loose here — it is a file, not a view. */
export interface DataExport {
  format: string
  exportedAt: string
  notes: string[]
  events: unknown[]
  sessions: unknown[]
  insights: unknown[]
  truncated: boolean
}

const get = <T>(path: string, auth: ParentAuth): Promise<T> =>
  request<T>(path, { context: 'user', token: auth.token, signal: auth.signal })

const post = <T>(path: string, auth: ParentAuth, body: unknown = {}): Promise<T> =>
  request<T>(path, { method: 'POST', body, context: 'user', token: auth.token, signal: auth.signal })

/* ─────────────────────────────────────────────────────────────── P3, D1 ── */

export const getConsent = (playerId: string, auth: ParentAuth = {}): Promise<ConsentView> =>
  get(`/consent/players/${encodeURIComponent(playerId)}`, auth)

/**
 * 🔴 THE EXPLICIT CONSENT ACTION — contract D1.
 *
 * A separate call from signing in, on purpose. **Signing in is not
 * consenting**: a row written by the login handler would document a decision
 * nobody made while turning retention on, which is the *"false green"* OD-50
 * refused.
 */
/** P3-31 — the notice to show before "Keep progress", for this child's school. */
export const getConsentNotice = (playerId: string, auth: ParentAuth = {}): Promise<ConsentNotice> =>
  get(`/consent/players/${encodeURIComponent(playerId)}/notice`, auth)

/**
 * 🔴 P3-31 — GRANTING CARRIES THE NOTICE VERSION THE PARENT WAS SHOWN. The server
 * refuses a stale one (409 `NOTICE_OUTDATED`) and stamps its own version and the
 * purposes on the record; nothing else about the decision comes from here.
 */
export const grantConsent = (playerId: string, noticeVersion: string, auth: ParentAuth = {}): Promise<ConsentView> =>
  post(`/consent/players/${encodeURIComponent(playerId)}`, auth, { noticeVersion })

export const revokeConsent = (playerId: string, auth: ParentAuth = {}): Promise<ConsentView> =>
  post(`/consent/players/${encodeURIComponent(playerId)}/revoke`, auth)

/* ─────────────────────────────────────────────────────────────────── S3 ── */

/** The school's consent dashboard. Admin only — see the backend controller. */
export const getClassConsent = (
  classId: string,
  auth: ParentAuth = {},
): Promise<ConsentClassRow[]> => get(`/classes/${encodeURIComponent(classId)}/consent`, auth)

/* ─────────────────────────────────────────────────────────────── P1, P2 ── */

/**
 * P1 — portability. *"A progress PDF is not a data export."*
 *
 * ⚠️ FETCHED RATHER THAN LINKED, for the reason `coverageCsvPath` records: a
 * plain `<a href>` carries cookies, and this API uses bearer tokens. So the
 * document comes back through the same client every other call uses, and the
 * screen turns it into a download itself (`downloadExport`).
 */
export const exportChildData = (playerId: string, auth: ParentAuth = {}): Promise<DataExport> =>
  get(`/rights/players/${encodeURIComponent(playerId)}/export.json`, auth)

export const eraseChildData = (playerId: string, auth: ParentAuth = {}): Promise<ErasureResult> =>
  post(`/rights/players/${encodeURIComponent(playerId)}/erase`, auth)

/**
 * Turn the fetched document into a saved file.
 *
 * 🔴 THE FILENAME CARRIES NO CHILD'S NAME. The server names it the same way and
 * for the same reason (`export-document.ts`): a file called
 * `Meera-Nair-export.json` sits in a downloads folder, a browser history, and
 * whatever the parent forwards it to.
 */
export function downloadExport(playerId: string, document_: DataExport): void {
  const day = document_.exportedAt.slice(0, 10)
  const blob = new Blob([JSON.stringify(document_, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `kalqy-data-export-${playerId}-${day}.json`
  anchor.click()
  // Released immediately: an object URL held after the click keeps a child's
  // full event stream alive in the tab's memory until navigation.
  URL.revokeObjectURL(url)
}
